import { Dispatch, SetStateAction, useState } from 'react'
import {
  ApolloError,
  ChangePasswordMutation,
  MutationChangePasswordArgs,
  UpdateUserProfileMutation,
  UpdateUserProfileMutationVariables,
  discardAuthToken,
  useMutation,
  useQuery,
} from '@lojinha/palantir'
import { useLojinhaContext } from '@lojinha/context'
import { ChangePasswordFormData } from '@lojinha/login/src/modules/change-password/types'
import { beholder } from '@lojinha/beholder'
import { clearCookies } from '@lojinha/helpers'
import { t } from '../../../dictionary'
import { EditProfileFormData } from '../form'
import {
  CHANGE_PASSWORD_MUTATION,
  GET_PROFILE_DATA,
  UPDATE_USER_MUTATION,
} from './queries'

type useProfileProps = {
  name: string
  email: string
  phoneNumber: string
  cpf: string
  loading: boolean
}

export const useProfile = (): useProfileProps => {
  const { isAuthenticated } = useLojinhaContext()

  const { data, loading } = useQuery(GET_PROFILE_DATA, {
    skip: !isAuthenticated,
  })

  const { name, email, phoneNumber, cpf } = data?.viewer?.user ?? ''

  return {
    loading,
    name,
    email,
    phoneNumber,
    cpf,
  }
}

type useEditProfileProps = {
  editUserProfile: (formData: EditProfileFormData) => void
  data?: UpdateUserProfileMutation | null
  error?: ApolloError
  loading: boolean
  showSuccessMessage: boolean
  setShowSuccessMessage: Dispatch<SetStateAction<boolean>>
  showErrorMessage: boolean
  setShowErrorMessage: Dispatch<SetStateAction<boolean>>
}

export const useEditProfile = (): useEditProfileProps => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  const [updateUserMutation, { error, loading }] = useMutation<
    UpdateUserProfileMutation,
    UpdateUserProfileMutationVariables
  >(UPDATE_USER_MUTATION, {
    onCompleted() {
      setShowSuccessMessage(true)
    },
    onError() {
      setShowErrorMessage(true)
    },
  })

  const editUserProfile = async (formData: EditProfileFormData) => {
    const { cpf, name, phoneNumber } = formData

    await updateUserMutation({
      variables: {
        input: {
          cpf,
          name,
          phoneNumber,
        },
      },
    })
  }

  return {
    editUserProfile,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    error,
    loading,
  }
}

export type ChangePasswordError = {
  type: string
  message: string
} | null

const sendChangePasswordEvent = (success: boolean) => {
  beholder.shot('structuredEvent', {
    category: 'my_profile',
    action: 'save_password_settings',
    property: success ? 'valid' : 'invalid',
  })
}

export const useChangePassword = () => {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<ChangePasswordError>(null)

  const logoutUser = () => {
    discardAuthToken()
    clearCookies()
    localStorage.clear()

    setTimeout(() => {
      window.location.href = '/identification'
    }, 1500)
  }

  const [changePasswordMutation, { loading }] = useMutation<
    ChangePasswordMutation,
    MutationChangePasswordArgs
  >(CHANGE_PASSWORD_MUTATION)

  const changePassword = async ({
    oldPassword,
    newPassword,
  }: ChangePasswordFormData) => {
    try {
      const { data } = await changePasswordMutation({
        variables: {
          input: {
            oldPassword,
            newPassword,
          },
        },
      })

      if (!data?.changePassword.success) {
        throw new Error(t.changePasswordError)
      }

      sendChangePasswordEvent(true)
      setSuccess(true)
      setError(null)
      logoutUser()
    } catch (err: any) {
      const { graphQLErrors, networkError } = err as ApolloError
      sendChangePasswordEvent(false)

      if (graphQLErrors?.[0]?.message === 'Senha atual incorreta.') {
        return setError({
          type: 'validation',
          message: t.wrongPassword,
        })
      }

      setError({
        type: networkError ? 'network' : 'unknown',
        message: networkError?.message || t.changePasswordError,
      })
    }
  }

  const reset = () => {
    setSuccess(false)
    setError(null)
  }

  return {
    changePassword,
    loading,
    success,
    error,
    reset,
  }
}
